<template>
    <renter-side-modal ref="sideModal" @closeRenterModalEvent="() => chatPublicId ? $emit('chatClosed') : $router.push({ name: 'chat-myrooms' })">
        <template #modal-header-button>
            <action-button icon="fa fa-circle-info" @click="showChatInfo()" :text="$t('[[[Szczegóły]]]')" />
        </template>
        <template #modal-header-title v-if="chatRoom?.result.propertyPublicId || chatRoom?.result?.taskPublicId">
            <div class="d-flex flex-column align-items-tart">
                <p class="mb-0" v-if="chatRoom?.result?.propertyPublicId">
                    {{ $t('[[[Nieruchomość]]]') }}: <router-link :to="{ name: 'renter-properties-info-item', params: { publicId: chatRoom.result.propertyPublicId } }">{{ chatRoom.result.propertyName }}</router-link>
                </p>
                <p v-if="chatRoom?.result?.taskPublicId">
                    {{ $t('[[[Zadanie]]]') }}: <router-link :to="{ name: 'renter-tasks-edit', params: { publicId: chatRoom.result.taskPublicId } }">{{ chatRoom.result.taskTitle }}</router-link>
                </p>
            </div>
        </template>
        <template #modal-content>
            <div class="chat">
                <div class="row">
                    <div class="col-md-9">
                        <ideo-form-group class="search-input">
                            <ideo-form-input class="mb-1" type="text" name="search" v-model="searchMessage.search"></ideo-form-input>
                            <action-button v-if="searchMessage.search" variant="" class="search-close m-0" icon="fa-solid fa-close" @click="closeSearchMessagesModal" />
                            <div v-if="searchMessage.search && searchQuery">{{ $t('[[[Wyszukiwana fraza:]]]') }} <strong>{{ searchQuery }}</strong></div>
                        </ideo-form-group>
                    </div>
                    <div class="col-md-3 d-flex align-items-start justify-content-end">
                        <action-button icon="fa-solid fa-search m-0" :text="$t('[[[Szukaj]]]')" @click="searchMessages" :disabled="!searchMessage.search" />
                    </div>
                </div>
                <div v-if="searchMessageList !== null" class="row">
                    <div v-for="message in searchMessageList?.result.slice().reverse()" :key="message.publicId">
                        <div v-if="message.isSystemMessage" class="chat__message chat__message--system d-flex justify-content-center">
                            <div class="chat__message-content d-inline-block text-center">
                                <div class="chat__message-text">{{ message.message }}</div>
                            </div>
                        </div>
                        <div v-else class="chat__message d-flex" :class="[message.userId === userName ? 'chat__message--sent justify-content-end ms-auto' : 'chat__message--received']">
                            <div class="chat__message-info text-muted" :class="{'text-end': message.userId === userName}">
                                <span v-if="message.userId !== userName" class="chat__message-author">{{ message.userName }}</span>
                                {{ $filters.date(message.date, 'yyyy-MM-dd HH:mm') }}
                            </div>
                            <div class="chat__message-content d-inline-block">
                                <message-photos :photos="message.photos" />
                                <div class="chat__message-text"><linkified :text="message.translatedMessage || message.message" /></div>
                                <translate
                                    v-if="message.userId !== userName"
                                    :translate-text="message.message"
                                    @translatedText="(translated) => messagesList.items[messageIndex].translatedMessage = translated"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else ref="chatContent" class="chat__content">
                    <div v-if="messagesList" class="chat__messages d-flex align-items-end">
                        <div class="w-100 scroll" ref="chatScroll" :style="{overflowY: loading ? 'hidden' : 'auto'}">
                            <template v-if="loading">
                                <div class="loading">{{ $t('[[[Ładuje...]]]') }}</div>
                                <div class="cushion"></div>
                            </template>
                            <div v-for="(message, messageIndex) in messagesList.items" :key="message.publicId">
                                <div v-if="message.isSystemMessage" class="chat__message chat__message--system d-flex justify-content-center">
                                    <div class="chat__message-content d-inline-block text-center">
                                        <div class="chat__message-text">{{ message.message }}</div>
                                    </div>
                                </div>
                                <div v-else class="chat__message d-flex" :class="[message.userId === userName ? 'chat__message--sent justify-content-end ms-auto' : 'chat__message--received']">
                                    <div class="chat__message-info text-muted" :class="{'text-end': message.userId === userName}">
                                        <span v-if="message.userId !== userName" class="chat__message-author">{{ message.userName }}</span>
                                        {{ $filters.date(message.date, 'yyyy-MM-dd HH:mm') }}
                                    </div>
                                    <div class="chat__message-content d-inline-block">
                                        <message-photos :photos="message.photos" />
                                        <div class="chat__message-text"><linkified :text="message.translatedMessage || message.message" /></div>
                                        <translate
                                            v-if="message.userId !== userName"
                                            :translate-text="message.message"
                                            @translatedText="(translated) => messagesList.items[messageIndex].translatedMessage = translated"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <ideo-button
                    class="scroll-bottom"
                    v-if="goToBottomVisible"
                    @click="scrollToBottom()"
                >
                    {{ $t('[[[Wróć na dół]]]') }}
                </ideo-button>
            </div>
            <ideo-modal ref="modal" :title="$t('[[[Informacje o czacie]]]')">
                <chat-details :chat-details="chatRoom.result" :chat-public-id="chatRoomPublicId" @onUpdate="(chatRoom) => loadChatRoom(chatRoom)" />
            </ideo-modal>
            <ideo-modal ref="photosModal" :title="$t('[[[Dodaj zdjęcia]]]')">
                <fileupload v-model="message.photos" :max-files="maxFiles" :accept="fileAccept"></fileupload>
            </ideo-modal>
        </template>
        <template #modal-footer>
            <p class="mb-0" v-show="message.photos.length">{{ $t('[[[Dodano zdjęć]]]') }}: {{ message.photos.length }}</p>
            <div v-if="!isUserMember" class="d-flex justify-content-center align-items-center p-3">
                <div class="text-center">
                    <p class="mb-3 text-muted">{{ $t('[[[Aby wysłać wiadomość, dołącz do czatu]]]') }}</p>
                    <ideo-button
                        @click="joinChat()"
                        :disabled="!canJoin"
                        variant="primary"
                        size="lg"
                    >
                        <i class="fa fa-plus me-2" v-if="!joiningChat"></i>
                        <i class="fa fa-spinner fa-spin me-2" v-if="joiningChat"></i>
                        {{ joiningChat ? $t('[[[Dołączanie...]]]') : $t('[[[Dołącz do czatu]]]') }}
                    </ideo-button>
                </div>
            </div>
            <div v-else class="chat__message-send d-flex">
                <ideo-form-textarea ref="messageField" v-model="message.message" maxlength="5000" @keydown.enter.prevent="send()" type="text" :placeholder="[isBlockedUser ? $t('[[[Zostałeś zablokowany, dodanie wiadomości jest niemożliwe]]]') : $t('[[[Treść wiadomości]]]')]" :disabled="isBlockedUser || sending" @paste="watchFieldPaste" />
                <ideo-dropdown up no-caret variant="primary" :title="$t('[[[Dodaj zdjęcia do wiadomości]]]')">
                    <template #button-content>
                        <i class="fa-solid fa-images"></i>
                    </template>
                    <div class="d-flex justify-content-center align-items-center flex-column gap-10">
                        <ideo-button class="m-0" @click.prevent="$refs.photosModal.show()"><i class="fa-solid fa-images mr-2"></i> {{ $t('[[[Dodaj zdjęcia]]]') }}</ideo-button>
                        <renter-camera class="m-0 mt-2" @onOpen="$refs.sideModal.hide()" @onClose="$refs.sideModal.show()" @photo-uploaded="(photo) => message.photos.push(photo)" @photo-uploading="(state) => loading = state" scope="chat" />
                    </div>
                </ideo-dropdown>
                <action-button icon="fa fa-send" @click="send()" :disabled="sending || loading" />
            </div>
            <small v-if="!isBlockedUser" class="d-block text-end mt-1" :class="(message.message?.length || 0) >= 5000 ? 'text-danger' : 'text-muted'">
                {{ message.message?.length || 0 }} / 5000
            </small>
        </template>
    </renter-side-modal>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { Prop, Watch } from '@/helpers/Decorators';
import { Pagination, Resource } from '@/helpers/Interfaces';
import ChatService, { ChatMessageFormModel, LastSeenMessageModel, MessagePublicListModel, MessageListByText } from '../services/ChatService';
import { ChatRoomModel } from '../services/ChatRoomService';
import ChatPager from  '@/plugins/chatPager';
import { Invoke, Listen } from '@/plugins/signalr';
import RenterSideModal from '@/modules/renter/components/RenterSideModal.vue';
import ChatDetails from './partials/ChatDetails.vue';
import MessagePhotos from './partials/MessagePhotos.vue';
import setAppTitle, { AppTitleEnum } from '@/modules/renter/common/helpers/appTitle';
import RenterCamera from '@/modules/renter/components/RenterCamera.vue';
import { Resource as FileResource } from '@/modules/core/files/services/StorageService';
import FileUploadHelper from '@/helpers/FileUpload';

@Options({
    components: { RenterSideModal, ChatDetails, RenterCamera, MessagePhotos },
    emits: ['chatClosed']
})
export default class ChatView extends Vue
{
    @Prop({ default: null }) public chatPublicId: string;

    private chatRoom: Resource<ChatRoomModel> = null;
    private messageField: any;
    public canJoin = false;

    private messagesList: Pagination<MessagePublicListModel> = null;
    private message: ChatMessageFormModel = {
        roomPublicId: null,
        message: '',
        photos: []
    };
    private lastSeenMessage: LastSeenMessageModel = {
        roomPublicId: null,
        lastSeenMessagePublicId: null
    };
    private searchMessage: MessageListByText = {
        roomPublicId: null,
        search: null,
        userId: null
    };
    private searchMessageList: Resource<MessagePublicListModel> = null;
    private searchQuery: string;
    private isLoadMoreEnable: boolean = false;
    private isBlockedUser: boolean = false;
    private isUserMember: boolean = false;
    private joiningChat: boolean = false;

    private startY: number;
    private startHeight: number;

    public loading: boolean = true;

    public checkUserMembership(): void
    {

        if (this.chatRoom?.result?.roomUsers)
        {
            const userId = this.$auth.user()?.id;

            this.isUserMember = this.chatRoom.result.roomUsers.some(user => user.id === userId);
        }
        else
        {
            this.isUserMember = false;
        }
    }

    public async joinChat(): Promise<void>
    {
        this.joiningChat = true;

        try
        {
            await ChatService.joinChat(this.chatPublicId);

            this.isUserMember = true;

            await this.loadChatRoom();

            await this.loadMessagesList();

            this.scrollToBottom();

            this.$alert.success(this.$t('[[[Pomyślnie dołączono do czatu]]]'));
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
        finally
        {
            this.joiningChat = false;
        }
    }
    public sending: boolean = false;
    public goToBottomVisible: boolean = false;

    private fileUploadHelper: FileUploadHelper;
    private fileAccept = ['image/jpeg', 'image/png'];
    private maxFiles = 3;

    private get userName(): number
    {
        return this.$auth.user()?.id;
    }

    private get chatRoomPublicId(): string
    {
        if (this.$route.params.public == "null") return '';

        return String(this.chatPublicId || this.$route.params.publicId || '');
    }

    private get chatContent(): HTMLDivElement
    {
        return this.$refs.chatContent as HTMLDivElement;
    }

    private get chatScroll(): HTMLDivElement
    {
        return this.$refs.chatScroll as HTMLDivElement;
    }

    private get resizer(): HTMLDivElement
    {
        return this.$refs.resizer as HTMLDivElement;
    }

    public async watchFieldPaste(event: ClipboardEvent): Promise<void>
    {
        const { result, rejected } = this.fileUploadHelper.handleClipboardImages(event.clipboardData.items);

        if (rejected.length > 0 )
        {
            this.$alert.warning(this.$t(
                '[[[Można dodawać tylko pliki typu: "%0".|||{0}|||]]]',
                [...this.fileAccept].join('", "')
            ));
        }

        if (!result.length) return;

        if (this.message.photos.length >= 3 )
        {
            return;
        }

        if (result.length > 0)
        {
            const { result: pushResult } = await this.fileUploadHelper.pushFiles(result);

            // @ts-ignore
            this.message.photos = [...this.message.photos, ...pushResult];
        }
    }

    public async mounted(): Promise<void>
    {
        this.fileUploadHelper = new FileUploadHelper([], [], this.fileAccept);
        this.$store.state.renter.chat.removeNotification = this.chatRoomPublicId;

        const ref: any = this.$refs.sideModal;

        this.messageField = this.$refs.messageField as HTMLTextAreaElement;

        const response = await this.loadChatRoom();

        setTimeout(async () =>
        {
            try
            {
                if (this.chatRoom?.result?.publicId)
                {
                    await this.openChat(this.chatRoom.result.publicId);
                }
            }
            catch (ex)
            {
                this.$log.debug(ex);
            }
        }, 1);

        ref.init(`${this.$t('[[[Czat]]]')}: ${this.chatRoom.result.name}`);

        if (response)
        {
            await this.loadMessagesList();
            this.chatScroll.addEventListener('scroll', this.scrollChat);
            await this.registerUser(this.chatRoom.result.publicId);

            this.scrollToBottom();
        }
    }

    public async unmounted(): Promise<void>
    {
        await this.closeChat(this.chatRoom.result.publicId);
    }

    @Watch('isLoadMoreEnable')
    private async onCheckedLoadMoreEnable(value: boolean): Promise<void>
    {
        if (value)
        {
            this.loading = true;
            await this.loadMore();
            this.loading = false;
        }
    }

    @Invoke('chat', 'RegisterUser')
    private async registerUser(roomPublicId: string): Promise<string>
    {
        return;
    }

    @Listen('chat')
    private async loadMessages(): Promise<void>
    {
        await this.loadNewer();
        this.scrollToBottom();
    }

    @Invoke('chat', 'OpenChat')
    public openChat: (roomPublicId: string) => Promise<void>;

    @Invoke('chat', 'CloseChat')
    public closeChat: (roomPublicId: string) => Promise<void>;

    private async loadChatRoom(chatRoom: Resource<ChatRoomModel> = null): Promise<boolean>
    {
        try
        {
            if (chatRoom)
                this.chatRoom = chatRoom;
            else
            {
                if (!this.chatRoomPublicId) return false;

                this.chatRoom = await ChatService.fetchRoom(this.chatRoomPublicId);
            }

            this.canJoin = this.chatRoom.meta.canJoin;
            this.checkUserMembership();

            return true;
        }
        catch (ex)
        {
            this.$log.debug(ex);

            return false;
        }
    }

    private async loadMessagesList(): Promise<void>
    {
        const pager = new ChatPager('', 20);

        this.loading = true;
        this.messagesList = await ChatService.getMessages(this.chatRoom.result.publicId, pager);
        this.loading = false;

        this.readMessage();
    }

    private async send(): Promise<void>
    {
        this.sending = true;
        this.message.roomPublicId = this.chatRoom.result.publicId;

        try
        {
            if (this.message.photos.length === 0 && this.message.message.length === 0)
            {
                this.$alert.error(this.$t('[[[Wpisz treść wiadomości.]]]'));
            }
            else
            {
                this.message.photos = await this.uploadPhotos(this.message.photos);

                const res = await ChatService.sendMessage(this.message);

                if (res.code == 200)
                {
                    this.message.message = '';
                    this.message.photos = [];
                    this.scrollToBottom();
                }

                if (!this.$channels['chat'].currentStatus)
                {
                    await this.loadMessages();
                }
            }

            this.lastMessageUserId = this.userName;
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: (ex: any) =>
                {
                    try
                    {
                        const errors = Object.values(ex.data.errors) as string[];

                        // Filename error ( e.g contains brackets)
                        this.$alert.error(errors[0][0]);
                    }
                    catch ( err )
                    {
                        console.log(err);
                    }


                }
            });
        }

        this.sending = false;
    }

    public lastMessageUserId: number = null;

    private async loadMore(): Promise<void>
    {
        if (this.messagesList.items.length < this.messagesList.totalRows)
        {
            const lastPublicId = this.messagesList.items[0].publicId;
            const pager = new ChatPager(lastPublicId, 20, 'showOlder');

            const newMessage = (await ChatService.getMessages(this.chatRoom.result.publicId, pager));

            this.messagesList.items = newMessage.items.concat(this.messagesList.items);
        }
    }

    private async loadNewer(): Promise<void>
    {
        let lastPublicId = '';

        if (this.messagesList.items.length > 0)
        {
            lastPublicId = this.messagesList.items[this.messagesList.items.length - 1].publicId;
        }

        const pager = new ChatPager(lastPublicId, 0, 'showNewer');

        const newMessage = (await ChatService.getMessages(this.chatRoom.result.publicId, pager));

        const [ newMessageItem ] = newMessage.items;

        if (newMessageItem && newMessageItem.userId !== this.lastMessageUserId)
            setAppTitle(AppTitleEnum.NewMessage, true);

        this.messagesList.items = this.messagesList.items.concat(newMessage.items);

        this.readMessage();
    }

    private async readMessage(): Promise<void>
    {
        const lastPublicId = this.messagesList?.items[this.messagesList.items.length - 1].publicId;

        if (this.chatRoom.result.lastSeenMessagePublicId !== lastPublicId)
        {
            this.lastSeenMessage.roomPublicId = this.chatRoom.result.publicId;
            this.lastSeenMessage.lastSeenMessagePublicId = lastPublicId;

            const result = await ChatService.updateLastSeenMessage(this.lastSeenMessage);

            if (result.code === 202)
            {
                this.chatRoom.result.lastSeenMessagePublicId = lastPublicId;
            }
        }
    }

    private async scrollChat(): Promise<void>
    {
        if (this.chatScroll.scrollTop < 60)
        {
            this.isLoadMoreEnable = true;
        }
        else
        {
            this.isLoadMoreEnable = false;
        }

        if (this.chatScroll.scrollTop + 1500 > this.chatScroll.scrollHeight)
            this.goToBottomVisible = false;
        else
            this.goToBottomVisible = true;
    }

    private scrollToBottom(): void
    {
        if (this.chatScroll)
            this.chatScroll.scrollTop = this.chatScroll.scrollHeight;
    }

    public showChatInfo(): void
    {
        const modalRef: any = this.$refs.modal;

        modalRef.show();
    }

    private getTimestampString(): string
    {
        const now = new Date();
        const formatTwoDigits = (num: number): string => num.toString().padStart(2, '0');

        return `${now.getFullYear()}${formatTwoDigits(now.getMonth() + 1)}${formatTwoDigits(now.getDate())}_${formatTwoDigits(now.getHours())}${formatTwoDigits(now.getMinutes())}${formatTwoDigits(now.getSeconds())}`;
    }

    private getPhotoExtension(filename: string): string
    {
        const splitName = filename.split('.');
        const extension = splitName.length > 1 ? splitName.pop() : null;

        return extension ? extension.toLowerCase() : 'jpg';
    }

    private async uploadPhotos(photosList: Array<any>): Promise<FileResource[]>
    {
        const photos = photosList.filter(photo => photo.publicId);
        const photosToUpload = photosList.filter(photo => photo.upload);

        const timestamp = this.getTimestampString();

        const responses = await Promise.all(
            photosToUpload.map((photo, index) =>
            {
                const extension = this.getPhotoExtension(photo.upload?.name || 'jpg');
                const newPhotoName = `image_${timestamp}${photosToUpload.length > 1 ? `_${index + 1}` : ''}.${extension}`;
                const renamedPhoto = new File([photo.upload], newPhotoName, { type: photo.upload.type });

                return ChatService.uploadPhoto(renamedPhoto, "default");
            })
        );

        return [...photos, ...responses];
    }

    public async searchMessages(): Promise<void>
    {
        this.loading = true;

        try
        {
            this.searchMessageList = await ChatService.getMessagesByText(this.chatRoomPublicId, this.searchMessage.search);
            this.searchQuery = this.searchMessage.search;
        }
        catch (ex)
        {
            this.$log.debug(ex);
        }
        finally
        {
            this.loading = false;
        }
    }

    public closeSearchMessagesModal(): void
    {
        this.searchMessageList = null;
        this.searchMessage.search = "";
        this.searchQuery = "";
    }
}
</script>
<style lang="scss">
.chat{
    height: 100%;

    &__content{
        height: 100%;
    }
    &__messages{
        height: 100%;

        & > div{
            height: 100%;
        }
    }
    &__message{
        flex-direction: column-reverse;
        max-width: 50%;
        padding: 10px 20px;
        border-radius: 20px;
        margin: 10px 0;

        &--sent{
            background: $primary;
            color: white;
            border-bottom-right-radius: 2px;

        }
        &--received{
            background: var(--light);
            border-bottom-left-radius: 2px;
        }

    &--system {
        margin: 5px auto;
        max-width: 80%;
        padding: 8px 16px;
        font-size: 0.9rem;
    }
}

.chat__message {
    &-info{
        margin-top: 5px;
        font-size: .7rem;
    }
        &-author{
            color: $gray-600;
        }

        &-send{
            margin-top: 10px;
        }
    }

    &__message-send{
        gap: 5px;
    }

    .loading{
        position: absolute;
        width: 100px;
        text-align: center;
        left: calc(50% - 50px);
        padding: 10px 20px;
        background: $white;
        border-radius: 5px;
        box-shadow: 0px 5px 10px rgba(0, 0, 0, .1);
    }
    .cushion{
        height: 50px;
    }
    .scroll-bottom{
        position: absolute;
        bottom: 100px;
        left: 50%;
        transform: translateX(-50%);
    }
    .search-input {
        position: relative;
        .search-close {
            position: absolute;
            right: 0;
            top: 0;
            border: none;
            &:hover, &:focus {
                border: none
            }
        }
    }
}
</style>
